import { Divider } from 'antd';
import { Icon } from '../../icon/icon';
import { useContext, useEffect, useState } from 'react';
import { DesignerPageContext } from '../../../pages/DesignerPage/DesignerPage';
import { roundTo } from '../../../utils/geometry/epsilon';
import { capitalize } from 'lodash';
import { mapListObjectByKey } from '../../../utils/data/map.utils';
import { filterByKeyAndValue } from '../../../utils/data/list.utils';
import { useTranslation } from 'react-i18next';
import {
  convertCodeToName,
  convertTypeToName,
} from '../../../utils/string/string.utils';
import { InsideType } from '../../../constants/enum';
import ButtonPrimaryMedium from '../../common/ButtonPrimary/ButtonPrimaryMedium';
import CardItem from '../../card/CardItem/CardItem';
import * as THREE from 'three';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';
import { getValueFromString } from '../../../utils/data/getValue.utils';
import TabComponent from '../../common/TabComponent/TabComponent';
import { convertForeignCurrency } from '../../../utils/currency/currency.utils';

const RenderPopup = ({ setOpen }) => {
  const [woodProducts, setWoodProduct] = useState([]);
  const [curveProducts, setCurveProduct] = useState([]);
  const [totalPrice, setTotalPrice] = useState({});
  const [count, setCount] = useState({});
  const [accessories, setAccessories] = useState([]);
  const [ledsDecor, setLedsDecor] = useState(0);
  const [lightSwitches, setLightSwitches] = useState(0);
  const [ledDrivers, setLedDrivers] = useState(0);
  const [countertop, setCountertop] = useState();
  const [backsplash, setBacksplash] = useState();
  const [mapModule, setMapModule] = useState([]);
  const [specialTexture, setSpecialTexture] = useState(false);
  const [tab, setTab] = useState('wood');

  const { sceneManager, handleSaveAllDesign, setShowCabinetTrimView } =
    useContext(DesignerPageContext);

  const { t } = useTranslation();

  useEffect(() => {
    const modules = sceneManager?.cabinetManager?.currentCabinet?.modules;
    if (modules) {
      const listModule = Array.from(modules).map(([, item]) => {
        return {
          id: item.id,
          cabinetType: item.cabinetType,
          material: item.material,
          thumbnail_url: item.thumbnail_url,
          moduleData: item.moduleData,
          type: item.cabinet?.type,
          textures: item.textures,
          listInside: item.listInside,
        };
      });

      const resultFilterWoodProducts = filterByKeyAndValue(
        'material',
        'wood',
        listModule
      );
      const resultMapObject = mapListObjectByKey(
        'cabinetType',
        resultFilterWoodProducts
      );
      setMapModule([...Object.entries(resultMapObject)]);
      setWoodProduct([...resultFilterWoodProducts]);

      const tempCount = {};
      const country_code = process.env.REACT_APP_COUNTRY_CODE ?? 'vn';

      const result = sceneManager?.getResultEstimatePrice();
      const registryCost = result?.registryCost?.[country_code];
      const registrySale = result?.registrySale?.[country_code];
      const aggregatedSaleTotals = result?.aggregatedSaleTotals?.[country_code];
      const detailSaleTotals = result?.detailSaleTotals?.[country_code];
      const totalPrice = {
        ...result?.total,
        costCurveBlocks: detailSaleTotals?.special?.curveBlocks,
        costEquipments: detailSaleTotals?.special?.equipments?.total,
        costFrame:
          aggregatedSaleTotals?.accessory +
          aggregatedSaleTotals?.component +
          aggregatedSaleTotals?.doors +
          aggregatedSaleTotals?.labor +
          aggregatedSaleTotals?.materials,
      };

      if (result) {
        setSpecialTexture(result?.useSpecialTexture ?? false);
        setTotalPrice(totalPrice);

        tempCount['hinge'] =
          Number(registryCost?.component?.hinges?.[0]?.quantity) +
          Number(registryCost?.component?.hinges135?.[0]?.quantity); //Bản lề

        tempCount['connector'] = Number(
          registryCost?.component?.connectors?.[0]?.quantity
        ); //Chốt cam

        tempCount['rail'] = Number(
          registryCost?.component?.rails?.[0]?.quantity
        ); //Ray

        // tempCount['liftSupport'] = Number(
        //   registryCost?.component?.liftSupport?.[0]?.quantity
        // ); //Ray

        setCount({ ...tempCount });

        setAccessories(
          (registryCost?.special?.equipments ?? [])
            .filter(item => item.cost)
            .map(item => ({
              name: item.name,
              price: item.unitPrice,
              material: item.material,
              count: Number(item.quantity) ?? 0,
              thumbnail_url: item.thumbnail_url,
            }))
            .reduce((prev, curr) => {
              const temp = [...prev];
              const index = temp.findIndex(
                i => i.name === curr.name && i.quantity === curr.quantity
              );
              if (index > -1) {
                temp[index].count += 1;
              } else {
                temp.push({ ...curr, count: 1 });
              }
              return temp;
            }, [])
            ?.sort(
              (a, b) =>
                !a.name?.localeCompare(b.name, 'vi', {
                  sensitivity: 'base',
                })
            )
        );

        if (registryCost?.accessory?.ledsDecor) {
          setLedsDecor(
            registryCost?.accessory?.ledsDecor?.reduce(
              (prev, curr) => prev + Number(curr.quantity),
              0
            ) ?? 0
          );
        } else {
          setLedsDecor(0);
        }
        setLedDrivers(
          registryCost?.accessory?.ledDrivers?.reduce(
            (prev, curr) => prev + Number(curr.quantity),
            0
          ) ?? 0
        );
        setLightSwitches(
          registryCost?.accessory?.lightSwitches?.reduce(
            (prev, curr) => prev + Number(curr.quantity),
            0
          ) ?? 0
        );
        setCurveProduct(aggregatedSaleTotals?.special?.curveBlocks ?? 0);
      }

      const countertopTexture = sceneManager.countertop?.texture;
      if (countertopTexture) {
        setCountertop({
          thumbnail_url: countertopTexture.url,
          name: countertopTexture.textureCode,
          width:
            registryCost?.special?.countertop?.reduce(
              (prev, curr) => prev + Number(curr.quantity),
              0
            ) ?? 0,
          total:
            registrySale?.special?.countertop?.reduce(
              (prev, curr) => prev + Number(curr.cost),
              0
            ) ?? 0,
          price: {
            price: Number(
              registryCost?.special?.countertop?.[0]?.unitPrice ?? 0
            ),
          },
        });
      } else {
        setCountertop(null);
      }

      const backsplashTexture = sceneManager.backsplash?.texture;
      if (backsplashTexture) {
        setBacksplash({
          thumbnail_url: backsplashTexture.url,
          name: backsplashTexture.textureCode,
          width:
            registryCost?.special?.backsplash?.reduce(
              (prev, curr) => prev + Number(curr.quantity),
              0
            ) ?? 0,
          total:
            registrySale?.special?.backsplash?.reduce(
              (prev, curr) => prev + Number(curr.cost),
              0
            ) ?? 0,
          price: {
            price: Number(
              registryCost?.special?.backsplash?.[0]?.unitPrice ?? 0
            ),
          },
        });
      } else {
        setBacksplash(null);
      }
    }
  }, [sceneManager]);

  const renderCardItem = item => {
    const textureFrame = item?.textures?.get('frame');
    const size = [];
    if (item?.moduleData?.width)
      size.push(roundTo(item.moduleData.width * 1000, 0));
    if (item?.moduleData?.depth)
      size.push(roundTo(item.moduleData.depth * 1000, 0));
    if (item?.moduleData?.height)
      size.push(roundTo(item.moduleData.height * 1000, 0));

    return (
      <>
        <CardItem
          icon={item.thumbnail_url}
          title={getValueFromString(item.moduleData?.name)}
          content={[
            <span className="line-clamp-1 truncate text-body-medium text-black-medium">
              {`${size.join(' x ')} mm,`}
            </span>,
            textureFrame ? (
              <span className="line-clamp-1 truncate text-body-medium">
                <div className="flex items-center gap-8 self-stretch">
                  <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                    {textureFrame.paintColor ?? textureFrame.textureCode}
                  </span>
                  <div className="flex w-[24px] h-[14px]">
                    {textureFrame.paintColor ? (
                      <span className={`bg-[${textureFrame.paintColor}]`} />
                    ) : (
                      <img src={textureFrame.textureUrl} alt="texture" />
                    )}
                  </div>
                </div>
              </span>
            ) : (
              <></>
            ),
          ]}
        />
      </>
    );
  };

  const renderSubCardItem = item => {
    const textureFrame = item?.textures?.get('frame');
    const textureDoor = item?.textures?.get('DOOR');
    const textureBedSlat = item?.textures?.get('vat_giuong');
    const textureGlass = item?.textures?.get('GLASS-PANEL');

    const insides = item.listInside
      ?.filter(item =>
        [
          InsideType.HPANEL,
          InsideType.VPANEL,
          InsideType.DRAWER,
          InsideType.U_DRAWER,
          InsideType.VCURVE,
          InsideType.HANDLE,
          // InsideType.LOCK,
          InsideType.VPANEL2,
          // InsideType.CLOTHES_HANGER,
          InsideType.HPANEL_RIGHT,
          InsideType.HPANEL_LEFT,
          InsideType.HPANEL_SHOE,
          InsideType.BED_SLAT,
          InsideType.LIFT_SUPPORT,
        ].includes(item.type)
      )
      .reduce((prev, curr) => {
        const index = prev.findIndex(i => i.name === curr.name);
        if (index >= 0) {
          const arr = [...prev];
          arr[index] = {
            ...arr[index],
            quantity: arr[index].quantity + 1,
          };
          return arr;
        }
        return [...prev, { ...curr, quantity: 1 }];
      }, []);
    const doors = item.listInside
      ?.filter(item => [InsideType.DOOR].includes(item.type))
      .reduce((prev, curr) => {
        const index = prev.findIndex(i => i.name === curr.name);
        const beveled = curr.gltf.userData.beveled;
        if (index >= 0) {
          const arr = [...prev];
          arr[index] = {
            ...arr[index],
            quantity: arr[index].quantity + 1,
          };
          return arr;
        }
        return [...prev, { ...curr, beveled, quantity: 1 }];
      }, []);
    const standardLock = item.listInside
      ?.filter(item => [InsideType.LOCK].includes(item.type))
      .reduce((prev, curr, currIndex) => {
        if (currIndex > 0) {
          const arr = [...prev];
          arr[0] = {
            ...arr[0],
            quantity: arr[0].quantity + 1,
          };
          return arr;
        }
        return [...prev, { ...curr, quantity: 1 }];
      }, []);
    const clothesHangers = item.listInside
      ?.filter(item => [InsideType.CLOTHES_HANGER].includes(item.type))
      .reduce((prev, curr) => {
        const box = new THREE.Box3().setFromObject(curr.gltf);
        const size = box.getSize(new THREE.Vector3());
        const length = roundTo(Math.max(size.x, size.y, size.z) * 1000, 0);
        const index = prev.findIndex(i => i.length === length);
        if (index >= 0) {
          const arr = [...prev];
          arr[index] = {
            ...arr[index],
            quantity: arr[index].quantity + 1,
          };
          return arr;
        }
        return [...prev, { ...curr, quantity: 1, length }];
      }, []);
    const leds = item.listInside
      ?.filter(item =>
        [InsideType.LED_RECESSED, InsideType.LED_CORNER].includes(item.type)
      )
      .reduce((prev, curr) => {
        const box = new THREE.Box3().setFromObject(curr.gltf);
        const size = box.getSize(new THREE.Vector3());
        const length = roundTo(Math.max(size.x, size.y, size.z), 3);
        const index = prev.findIndex(
          i => i.length === curr.length && i.name === curr.name
        );
        if (index >= 0) {
          const arr = [...prev];
          arr[index] = {
            ...arr[index],
            quantity: arr[index].quantity + 1,
          };
          return arr;
        }
        return [...prev, { ...curr, quantity: 1, length }];
      }, []);
    const legsKhungSat = item.listInside
      ?.filter(item => [InsideType.KHUNG_SAT].includes(item.type))
      .reduce((prev, curr) => {
        const box = new THREE.Box3().setFromObject(curr.gltf);
        const size = box.getSize(new THREE.Vector3());
        const orientation = GeometryUtils.getModuleOrientation(curr.gltf);
        if (['ROTATED_270', 'ROTATED_90'].includes(orientation)) {
          const length = roundTo(size.x * 1000, 0);
          const index = prev.findIndex(i => i.length === length);
          if (index >= 0) {
            const arr = [...prev];
            arr[index] = {
              ...arr[index],
              quantity: arr[index].quantity + 1,
            };
            return arr;
          }
          return [...prev, { ...curr, quantity: 1, length }];
        } else if (['NORMAL', 'ROTATED_180'].includes(orientation)) {
          const length = roundTo(size.z * 1000, 0);
          const index = prev.findIndex(i => i.length === length);
          if (index >= 0) {
            const arr = [...prev];
            arr[index] = {
              ...arr[index],
              quantity: arr[index].quantity + 1,
            };
            return arr;
          }
          return [...prev, { ...curr, quantity: 1, length }];
        }
      }, []);
    const legsRoi = item.listInside
      ?.filter(item => [InsideType.CHAN_ROI_TV].includes(item.type))
      .reduce((prev, curr) => {
        const index = prev.findIndex(i => i.name === curr.name);
        if (index >= 0) {
          const arr = [...prev];
          arr[index] = {
            ...arr[index],
            quantity: arr[index].quantity + 1,
          };
          return arr;
        }
        return [...prev, { ...curr, quantity: 1 }];
      }, []);
    return (
      <>
        {insides?.map((item, index) => {
          let paintColor;
          let textureUrl;
          let textureCode;
          const content = [];
          const name = item.name ?? convertTypeToName(item.type);
          let price;

          if (item.type === InsideType.BED_SLAT) {
            paintColor = textureBedSlat?.paintColor;
            textureUrl = textureBedSlat?.textureUrl;
            textureCode = textureBedSlat?.textureCode;
          } else if (
            [
              InsideType.HPANEL,
              InsideType.VPANEL,
              InsideType.DRAWER,
              InsideType.U_DRAWER,
              InsideType.VCURVE,
              InsideType.VPANEL2,
              InsideType.HPANEL_RIGHT,
              InsideType.HPANEL_LEFT,
            ].includes(item.type)
          ) {
            paintColor = textureFrame?.paintColor;
            textureUrl = textureFrame?.textureUrl;
            textureCode = textureFrame?.textureCode;
          }

          if (textureUrl && textureCode) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium">
                <div className="flex items-center gap-8 self-stretch">
                  <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                    {textureCode}
                  </span>
                  <div className="flex w-[24px] h-[14px]">
                    <img src={textureUrl} alt="texture" />
                  </div>
                </div>
              </span>
            );
          }
          if (paintColor) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium">
                <div className="flex items-center gap-8 self-stretch">
                  <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                    {paintColor}
                  </span>
                  <div className="flex w-[24px] h-[14px]">
                    <span className={`bg-[${paintColor}]`}></span>
                  </div>
                </div>
              </span>
            );
          }
          if (price) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium-emphasized text-black-medium">
                {convertForeignCurrency(item.price)}
              </span>
            );
          }
          return (
            <CardItem
              classNames={{ root: 'ps-[40px]' }}
              key={index}
              icon={item.thumbnail_url}
              title={getValueFromString(name)}
              quantity={item.quantity}
              content={content}
            />
          );
        })}
        {doors?.map((item, index) => {
          let paintColor;
          let textureUrl;
          let textureCode;
          const content = [];
          const name = item.name ?? convertTypeToName(item.type);
          const beveled = item.beveled;
          if (item.type === InsideType.DOOR) {
            paintColor = textureDoor?.paintColor;
            textureUrl = textureDoor?.textureUrl;
            textureCode = textureDoor?.textureCode;
          }

          if (textureUrl && textureCode) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium">
                <div className="flex items-center gap-8 self-stretch">
                  <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                    {textureCode}
                  </span>
                  <div className="flex w-[24px] h-[14px]">
                    <img src={textureUrl} alt="texture" />
                  </div>
                  {(() => {
                    if (item.materialType === 'nhom_kinh') {
                      return (
                        <span className="line-clamp-1 truncate text-body-medium">
                          <div className="flex items-center gap-8 self-stretch">
                            <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                              20*21 mm
                            </span>
                          </div>
                        </span>
                      );
                    }
                  })()}
                </div>
              </span>
            );
          }
          if (paintColor) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium">
                <div className="flex items-center gap-8 self-stretch">
                  <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                    {paintColor}
                  </span>
                  <div className="flex w-[24px] h-[14px]">
                    <span className={`bg-[${paintColor}]`}></span>
                  </div>
                </div>
              </span>
            );
          }
          if (textureGlass) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium">
                <div className="flex items-center gap-8 self-stretch">
                  <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                    {getValueFromString(textureGlass.textureCode)}
                  </span>
                  <div className="flex w-[24px] h-[14px]">
                    <img src={textureGlass.textureUrl} alt="texture" />
                  </div>
                  {(() => {
                    if (item.materialType === 'nhom_kinh') {
                      return (
                        <span className="line-clamp-1 truncate text-body-medium">
                          <div className="flex items-center gap-8 self-stretch">
                            <span className="text-body-medium text-black-medium ellipsis-multiline-1">
                              5 mm
                            </span>
                          </div>
                        </span>
                      );
                    }
                  })()}
                </div>
              </span>
            );
          }
          if (beveled) {
            content.push(
              <span className="line-clamp-1 truncate text-body-small text-[#258FD2]">
                *{t('No handle')}
              </span>
            );
          } else {
            content.push(
              <span className="line-clamp-1 truncate text-body-small text-[#258FD2]">
                *{t('With handle')}
              </span>
            );
          }
          return (
            <CardItem
              classNames={{ root: 'ps-[40px]' }}
              key={index}
              icon={item.thumbnail_url}
              title={getValueFromString(name)}
              quantity={item.quantity}
              content={
                <div className="flex flex-col items-start">{content}</div>
              }
            />
          );
        })}
        {standardLock?.map((item, index) => {
          return (
            <CardItem
              classNames={{ root: 'ps-[40px]' }}
              key={index}
              icon={item.thumbnail_url}
              title={getValueFromString(item.name)}
              quantity={item.quantity}
              content={<></>}
            />
          );
        })}
        {clothesHangers?.map((item, index) => {
          const name = `${item.name ?? convertTypeToName(item.type)} - ${item.length}mm`;
          return (
            <CardItem
              classNames={{ root: 'ps-[40px]' }}
              key={index}
              icon={item.thumbnail_url}
              title={getValueFromString(name)}
              quantity={item.quantity}
              content={
                <div className="flex flex-col items-start gap-[6px]">
                  <span className="text-body-medium text-black-medium">
                    {item.length} mm
                  </span>
                </div>
              }
            />
          );
        })}
        {leds?.map((item, index) => {
          const name = `${item.name ?? 'LED'}`;
          return (
            <CardItem
              classNames={{ root: 'ps-[40px]' }}
              key={index}
              icon={item.thumbnail_url}
              title={getValueFromString(name)}
              quantity={item.quantity}
              content={
                <div className="flex flex-col items-start gap-[6px]">
                  <span className="text-body-medium text-black-medium">
                    {item.length * 1000} mm
                  </span>
                </div>
              }
            />
          );
        })}
        {legsRoi?.map((item, index) => {
          const content = [];
          const name = item.name ?? convertTypeToName(item.type);
          const price = item.price ?? 0;
          if (price) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium-emphasized text-black-medium">
                {convertForeignCurrency(price)}
              </span>
            );
          }
          return (
            <CardItem
              classNames={{ root: 'ps-[40px]' }}
              key={index}
              icon={item.thumbnail_url}
              title={getValueFromString(name)}
              quantity={item.quantity}
              content={
                <div className="flex flex-col items-start">{content}</div>
              }
            />
          );
        })}
        {legsKhungSat?.map((item, index) => {
          const content = [];
          const name = `${item.name ?? convertTypeToName(item.type)} - ${item.length}mm`;
          const price = item.price || 0;
          if (price) {
            content.push(
              <span className="line-clamp-1 truncate text-body-medium-emphasized text-black-medium">
                {convertForeignCurrency(((price ?? 0) * item.length) / 1000)}
              </span>
            );
          }
          return (
            <CardItem
              classNames={{ root: 'ps-[40px]' }}
              key={index}
              icon={item.thumbnail_url}
              title={getValueFromString(name)}
              quantity={item.quantity}
              content={
                <div className="flex flex-col items-start">{content}</div>
              }
            />
          );
        })}
      </>
    );
  };

  const renderWoodDetail = () => {
    return (
      <div className="flex flex-col gap-[16px] items-center justify-start self-stretch">
        {woodProducts.length ? (
          <div className="flex flex-col items-start gap-16 self-stretch">
            <div className="flex py-[8px] px-[16px] gap-[8px] justify-start flex-col items-center self-stretch rounded-[8px] bg-primary-primaryFill">
              <div className="flex justify-between items-center self-stretch">
                <div className="flex flex-col items-start gap-2">
                  <span className="text-title-small">{t('Wood product')}</span>
                  <span className="text-body-small text-black/60">
                    {`${woodProducts.length} ${t('Product')}`}
                  </span>
                </div>
                <div className="flex gap-[4px]">
                  <span className="text-body-large emphasized text-black-high">
                    {convertForeignCurrency(
                      (() => {
                        if (
                          !sceneManager?.skipSpecialTexture &&
                          specialTexture
                        ) {
                          return roundTo(
                            totalPrice?.costFrame +
                              totalPrice?.costCurveBlocks -
                              (totalPrice?.totalMaterials?.costBoards ?? 0) *
                                0.05,
                            0
                          );
                        } else {
                          return roundTo(
                            totalPrice?.costFrame + totalPrice?.costCurveBlocks,
                            0
                          );
                        }
                      })()
                    )}
                  </span>
                </div>
              </div>
            </div>

            {Object.entries(mapModule).map(([, values], index) => {
              return (
                <div
                  key={index}
                  className="flex flex-col items-start gap-8 self-stretch"
                >
                  <span className="text-title-small">
                    {t(capitalize(convertCodeToName(values[0])))}
                  </span>
                  <div className="flex flex-col items-start self-stretch p-[8px] gap-[8px] rounded-[8px] border-[1px] border-[#E0E0E0] border-solid bg-white">
                    {values[1].map((item, i) => {
                      return (
                        <div
                          key={i}
                          className="flex flex-col p-[8px] items-start gap-[8px] self-stretch"
                        >
                          {renderCardItem(item)}
                          {renderSubCardItem(item)}
                          {values[1].length - 1 !== i ? (
                            <Divider className="m-0 !p-0" type="horizontal" />
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {lightSwitches || ledDrivers || ledsDecor ? (
              <div className="flex flex-col items-start gap-8 self-stretch">
                <span className="text-title-small">LED</span>
                <div className="flex p-16 flex-col items-start self-stretch rounded-[8px] bg-white">
                  {ledsDecor > 0 && (
                    <CardItem
                      icon={'/assets/images/led.png'}
                      title={'LED decor'}
                      content={[
                        <>
                          <div className="flex flex-col items-start gap-[4px] self-stretch">
                            <span className="text-body-medium text-black-medium">
                              {roundTo(ledsDecor * 1000, 0)} mm
                            </span>
                          </div>
                        </>,
                      ]}
                      quantity={undefined}
                    />
                  )}
                  {lightSwitches && (
                    <CardItem
                      icon={'/assets/images/switch.jpg'}
                      title={t('Switch')}
                      content={[]}
                      quantity={lightSwitches}
                    />
                  )}
                  {ledDrivers && (
                    <CardItem
                      icon={'/assets/images/driver.jpg'}
                      title={t('Driver')}
                      content={[]}
                      quantity={ledDrivers}
                    />
                  )}
                </div>
              </div>
            ) : (
              <></>
            )}
            {count['hinge'] || count['connector'] || count['rail'] ? (
              <div className="flex flex-col items-start gap-8 self-stretch">
                <span className="text-title-small">{t('Components')}</span>
                <div className="flex p-16 flex-col items-start self-stretch rounded-[8px] bg-white">
                  {count['hinge'] ? (
                    <CardItem
                      icon={'/assets/images/banle.png'}
                      title={t('Hinges')}
                      content={
                        [
                          // <span className="line-clamp-1 truncate text-body-medium text-black-medium">
                          //   {sceneManager?.nestingController?.hingePrice?.price?.toLocaleString(
                          //     'vi-VN',
                          //     {
                          //       style: 'currency',
                          //       currency: 'VND',
                          //     }
                          //   )}
                          // </span>,
                        ]
                      }
                      quantity={count['hinge']}
                    />
                  ) : (
                    <></>
                  )}
                  {count['connector'] ? (
                    <CardItem
                      icon={'/assets/images/chotcam.png'}
                      title={t('Connectors')}
                      content={
                        [
                          // <span className="line-clamp-1 truncate text-body-medium text-black-medium">
                          //   {sceneManager?.nestingController?.connectorPrice?.price?.toLocaleString(
                          //     'vi-VN',
                          //     {
                          //       style: 'currency',
                          //       currency: 'VND',
                          //     }
                          //   )}
                          // </span>,
                        ]
                      }
                      quantity={count['connector']}
                    />
                  ) : (
                    <></>
                  )}
                  {count['rail'] ? (
                    <CardItem
                      icon={'/assets/images/ray.png'}
                      title={t('Rails')}
                      content={
                        [
                          // <span className="line-clamp-1 truncate text-body-medium text-black-medium">
                          //   {sceneManager?.nestingController?.railPrice?.price?.toLocaleString(
                          //     'vi-VN',
                          //     {
                          //       style: 'currency',
                          //       currency: 'VND',
                          //     }
                          //   )}
                          // </span>,
                        ]
                      }
                      quantity={count['rail']}
                    />
                  ) : (
                    <></>
                  )}
                  {/* {count['liftSupport'] ? (
                    <CardItem
                      icon={'/assets/images/lift-support.jpg'}
                      title={t('Lift Support')}
                      content={
                        [
                          // <span className="line-clamp-1 truncate text-body-medium text-black-medium">
                          //   {sceneManager?.nestingController?.railPrice?.price?.toLocaleString(
                          //     'vi-VN',
                          //     {
                          //       style: 'currency',
                          //       currency: 'VND',
                          //     }
                          //   )}
                          // </span>,
                        ]
                      }
                      quantity={count['liftSupport']}
                    />
                  ) : (
                    <></>
                  )} */}
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {countertop ? (
          <div className="flex flex-col items-start gap-16 self-stretch">
            <div className="flex py-[8px] px-[16px] justify-between items-center self-stretch rounded-[8px] bg-primary-primaryFill">
              <div className="flex justify-between grow items-center gap-[2px]">
                <div className="flex flex-col items-start gap-2">
                  <span className="text-title-small">{t('Countertop')}</span>
                </div>
                <div className="flex gap-[4px]">
                  <span className="text-body-large emphasized text-black/80">
                    {convertForeignCurrency(countertop?.total)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-start gap-8 self-stretch">
              <div className="flex p-16 flex-col items-start self-stretch rounded-[8px] bg-white">
                {countertop ? (
                  <CardItem
                    icon={countertop.thumbnail_url}
                    title={countertop.name}
                    content={[
                      <span className="line-clamp-1 truncate text-body-medium text-black-medium min-w-full">
                        {roundTo(countertop.width * 1000, 0)} mm
                      </span>,
                      <span className="line-clamp-1 truncate text-body-medium-emphasized text-black-medium">
                        {convertForeignCurrency(countertop.price?.price)}
                      </span>,
                    ]}
                  />
                ) : (
                  <></>
                )}
              </div>
            </div>
          </div>
        ) : null}
        {backsplash ? (
          <div className="flex flex-col items-start gap-16 self-stretch">
            <div className="flex py-[8px] px-[16px] justify-between items-center self-stretch rounded-[8px] bg-primary-primaryFill">
              <div className="flex justify-between grow items-center gap-[2px]">
                <div className="flex flex-col items-start gap-2">
                  <span className="text-title-small">{t('Backsplash')}</span>
                </div>
                <div className="flex gap-[4px]">
                  <span className="text-body-large emphasized text-black/80">
                    {convertForeignCurrency(backsplash?.total)}
                  </span>
                </div>
              </div>
            </div>
            {backsplash ? (
              <div className="flex flex-col items-start gap-8 self-stretch">
                <div className="flex p-16 flex-col items-start self-stretch rounded-[8px] bg-white">
                  {backsplash ? (
                    <CardItem
                      icon={backsplash.thumbnail_url}
                      title={backsplash.name}
                      content={[
                        <span className="line-clamp-1 truncate text-body-medium text-black-medium min-w-full">
                          {roundTo(backsplash.width * 1000, 0)} mm
                        </span>,
                        <span className="line-clamp-1 truncate text-body-medium-emphasized text-black-medium">
                          {convertForeignCurrency(backsplash.price?.price)}
                        </span>,
                      ]}
                    />
                  ) : (
                    <></>
                  )}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderEquipmentsDetail = () => {
    return (
      <div className="flex flex-col gap-[16px] items-center justify-start self-stretch">
        {accessories?.length ? (
          <div className="flex flex-col items-start gap-16 self-stretch">
            <div className="flex py-[8px] px-[16px] justify-between items-center self-stretch rounded-[8px] bg-primary-primaryFill">
              <div className="flex flex-col items-start gap-2">
                <span className="text-title-small">
                  {t('Accessories and Equipments')}
                </span>
                <span className="text-body-small text-black/60">
                  {`${accessories.length} ${t('Product')}`}
                </span>
              </div>
              <div className="flex gap-[4px]">
                <span className="text-body-large emphasized text-black/80">
                  {convertForeignCurrency(totalPrice?.costEquipments)}
                </span>
              </div>
            </div>
            {accessories?.length ? (
              <div className="flex flex-col items-start gap-8 self-stretch">
                <div className="flex p-16 flex-col items-start self-stretch rounded-[8px] bg-white">
                  {accessories?.map((item, index) => {
                    const content = [];
                    if (item.material) {
                      content.push(
                        <span className="line-clamp-1 truncate text-body-medium text-black-medium">
                          {item.material}
                        </span>
                      );
                    }
                    if (item.price) {
                      content.push(
                        <span className="line-clamp-1 truncate text-body-medium-emphasized text-black-medium">
                          {convertForeignCurrency(item.price)}
                        </span>
                      );
                    }
                    return (
                      <CardItem
                        key={index}
                        icon={item.thumbnail_url}
                        title={item.name ?? 'Accessories'}
                        content={content}
                        quantity={item.count}
                      />
                    );
                  })}
                </div>
              </div>
            ) : (
              <></>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  if (mapModule.length)
    return (
      <>
        <div className="flex flex-col items-start grow self-stretch overflow-auto max-h-[570px]">
          <TabComponent
            items={[
              {
                key: 'wood',
                label: (
                  <div className="flex items-center justify-start gap-[8px]">
                    <span className="w-[20px] h-[20px] flex items-center justify-center">
                      <Icon.ICStack />
                    </span>
                    <span className="text-body-large font-semibold">
                      {t('Wood product')}
                    </span>
                  </div>
                ),
              },
              {
                key: 'accessoriesAndEquipments',
                label: (
                  <div className="flex items-center justify-start gap-[8px]">
                    <span className="w-[20px] h-[20px] flex items-center justify-center">
                      <Icon.ICCountertops />
                    </span>
                    <span className="text-body-large font-semibold">
                      {t('Accessories and Equipments')}
                    </span>
                  </div>
                ),
                disabled: !accessories?.length,
              },
            ]}
            style={{ width: '100%' }}
            className={
              'px-[24px] py-[12px] [&_.ant-tabs-content-holder]:hidden'
            }
            defaultActiveKey={tab}
            onChange={value => setTab(value)}
          />
          <div className="flex flex-col gap-[16px] p-[24px] self-stretch grow bg-backgrounds-timeline">
            {tab === 'wood' && renderWoodDetail()}
            {tab === 'accessoriesAndEquipments' && renderEquipmentsDetail()}
          </div>
        </div>
        <div
          className="flex p-16 justify-end items-center gap-16 self-stretch"
          style={{ borderTop: '1px solid #CFD1D2' }}
        >
          <ButtonPrimaryMedium
            title={t('Next')}
            onClick={() => {
              setOpen(false);
              setShowCabinetTrimView(true);
              handleSaveAllDesign({ complete: true });
            }}
          />
        </div>
      </>
    );

  return null;
};

export default RenderPopup;
